import {
  getImageFromMedias,
  isDefined,
  OneMedia,
  SearchParams,
} from '@prismamedia/one-components';
import { formatISO, set } from 'date-fns';
import { generatePath } from 'react-router-dom';
import {
  RecipeStatus,
  RecipeWhereInput,
} from '../../__generated__/queries-recipe';
import {
  ArticleFilterSearchInput,
  ArticleFormat,
  BrandKey,
} from '../../__generated__/queries-web';
import { paths } from '../../routing/Router/paths';
import { getBrandOptions } from '../../utils/brands';
import { getImageFromArticleBody } from '../../utils/getImageFromArticleBody';
import { Filters, Format } from './FiltersDrawer/utils';

export const PAID_ARTICLE = 'Article payant';

const formatToArticleFormatMap: Record<Format, ArticleFormat | undefined> = {
  [Format.Article]: ArticleFormat.Rich,
  [Format.Slideshow]: ArticleFormat.Slideshow,
  [Format.Recipe]: undefined,
};

export const articleFormatToFormatMap: Record<
  ArticleFormat,
  Format | undefined
> = {
  [ArticleFormat.Rich]: Format.Article,
  [ArticleFormat.Slideshow]: Format.Slideshow,
  [ArticleFormat.Video]: undefined,
};

export const ignoreArticleQuery = (filters: Filters) =>
  !filters.format?.includes(Format.Article) &&
  !filters.format?.includes(Format.Slideshow);

export const ignoreRecipeQuery = (
  filters: Filters,
  brandKey: BrandKey | undefined,
) =>
  !filters.format?.includes(Format.Recipe) ||
  !!filters.sourceIds?.length ||
  !!filters.qualifierIds?.length ||
  (brandKey && !getBrandOptions(brandKey).isRecipe);

export const getWhere = (
  filters: Filters,
  searchParams: SearchParams,
  brandKey: BrandKey | undefined,
) => {
  const startDate =
    filters.startDate &&
    set(new Date(filters.startDate), { hours: 0, minutes: 0, seconds: 0 });

  const endDate =
    filters.endDate &&
    set(new Date(filters.endDate), {
      hours: 23,
      minutes: 59,
      seconds: 59,
    });

  const articleWhere: ArticleFilterSearchInput = {
    publishedAt_gte: startDate && formatISO(startDate),
    publishedAt_lte: endDate && formatISO(endDate),
    format_in: filters.format
      ?.map((format) => formatToArticleFormatMap[format])
      .filter(isDefined),
    brandKey,
    status_in: filters.status,
    authors_some: filters.authorIds?.length
      ? { author: { id_in: filters.authorIds } }
      : undefined,
    tags_some: filters.tagIds?.length
      ? { tag: { id_in: filters.tagIds } }
      : undefined,
    source: filters.sourceIds?.length
      ? { id_in: filters.sourceIds }
      : undefined,
    categories_some: filters.categoryIds?.length
      ? { category: { id_in: filters.categoryIds } }
      : undefined,
    qualifiers_some: filters.qualifierIds?.length
      ? { qualifier: { id_in: filters.qualifierIds } }
      : undefined,
    OR: [
      { id: searchParams.search },
      { title: searchParams.search },
      {
        urls_some: searchParams.search
          ? { path: searchParams.search }
          : undefined,
      },
    ],
  };

  const recipeWhere: RecipeWhereInput = {
    publishedAt_gte: startDate && formatISO(startDate),
    publishedAt_lte: endDate && formatISO(endDate),
    brandKey,
    status_in: (filters.status?.filter((status) =>
      Object.values(RecipeStatus).includes((status as unknown) as RecipeStatus),
    ) as unknown) as RecipeStatus[],
    author_in: filters.authorIds?.length ? filters.authorIds : undefined,
    recipeTags_some: filters.tagIds?.length
      ? { tag: { id_in: filters.tagIds } }
      : undefined,
    recipeCategories_some: filters.categoryIds?.length
      ? { category: { id_in: filters.categoryIds } }
      : undefined,
    OR: [
      { id: searchParams.search },
      { title_contains: searchParams.search },
      {
        recipeUrls_some: searchParams.search
          ? { url: { path: searchParams.search } }
          : undefined,
      },
    ],
  };

  return { articleWhere, recipeWhere };
};

export const getItemPath = (
  format: Format | undefined,
  brandKey: string,
  id: string,
) => {
  if (!format) return undefined;

  if (format === Format.Recipe) {
    return `${config.FRONT_ONE_RECIPE}/${brandKey}/edit/${id}`;
  }

  if (format === Format.Slideshow) {
    return generatePath(paths.SLIDESHOW_EDIT, { id });
  }

  return generatePath(paths.ARTICLE_EDIT, {
    brandKey,
    articleType: 'article',
    id,
  });
};

export const getItemImage = (
  format: Format | undefined,
  medias: OneMedia[] | null | undefined,
  body: string | null | undefined,
) => {
  switch (format) {
    case Format.Slideshow:
      return getImageFromArticleBody(body || null, true);
    default:
      return getImageFromMedias(medias).mediaForDisplay;
  }
};
